import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "../../paths";
import logo from "../../assets/logo.png";

const Header = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (target) => {
    setOpen(false);
    if (target.startsWith("#")) {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(target);
    }
  };

  const navLinks = [
    { label: "About", target: paths.about },
    { label: "Rulebook", target: paths.rulebook },
  ];

  return (
    <header className="relative flex items-center justify-between px-20 max-xl:px-12 max-lg:px-10 max-md:px-6 max-sm:px-5 py-4 bg-transparent w-full box-border">
      {/* Adjusted speeds here to be visibly slower and smoother */}
      <style>{`
        /* HOVER IN (Slower): Green stretches first, Orange follows, Blue covers last */
        .group:hover .line-blue { transition-duration: 900ms !important; }
        .group:hover .line-orange { transition-duration: 700ms !important; }
        .group:hover .line-green { transition-duration: 500ms !important; }

        /* HOVER OUT (Slower): Blue collapses first, Orange follows, Green leaves last */
        .line-blue { transition-duration: 500ms !important; }
        .line-orange { transition-duration: 700ms !important; }
        .line-green { transition-duration: 900ms !important; }
      `}</style>

      {/* Logo */}
      <button onClick={() => go("/")} className="flex-shrink-0 flex items-center">
        <img src={logo} alt="Logo" className="h-8 w-auto block cursor-pointer" />
      </button>

      {/* Desktop nav */}
      <nav className="hidden md:flex items-center gap-10 lg:gap-12">
        {navLinks.map((l) => (
          <button
            key={l.label}
            onClick={() => go(l.target)}
            className="relative group text-white font-montserrat transition-colors whitespace-nowrap text-base lg:text-[17px] font-medium hover:text-gray-300"
          >
            <span className="relative z-10">{l.label}</span>
            
            {/* 1. GREEN LAYER (Bottom) */}
            <span
              aria-hidden="true"
              className="line-green absolute left-0 -bottom-1 h-[3px] w-full bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100"
            />
            
            {/* 2. ORANGE LAYER (Middle) */}
            <span
              aria-hidden="true"
              className="line-orange absolute left-0 -bottom-1 h-[3px] w-full bg-[#CC5e15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100"
            />

            {/* 3. MAIN BLUE LAYER (Top) */}
            <span
              aria-hidden="true"
              className="line-blue absolute left-0 -bottom-1 h-[3px] w-full bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100"
            />
          </button>
        ))}

        {/* Updated Sign In Button with Full Background Slide Effect */}
        <button
          onClick={() => go(paths.login)}
          className="relative group px-10 py-2 border-[3px] border-solid border-white text-white font-montserrat bg-transparent transition-all duration-300 whitespace-nowrap font-medium text-base lg:text-[17px] cursor-pointer overflow-hidden hover:border-[#019D9D]"
        >
          {/* Text layer pulled above the background slides */}
          <span className="relative z-40 transition-colors duration-500 group-hover:text-white">
            Sign In
          </span>

          {/* 1. GREEN LAYER (Bottom) */}
          <span
            aria-hidden="true"
            className="line-green absolute inset-0 bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100"
          />

          {/* 2. ORANGE LAYER (Middle) */}
          <span
            aria-hidden="true"
            className="line-orange absolute inset-0 bg-[#CC5E15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100"
          />

          {/* 3. MAIN BLUE LAYER (Top) */}
          <span
            aria-hidden="true"
            className="line-blue absolute inset-0 bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100"
          />
        </button>
      </nav>

      {/* Mobile hamburger */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="md:hidden flex flex-col gap-[5px] p-2"
        aria-label="Toggle menu"
        aria-expanded={open}
      >
        <span className={`block h-0.5 w-6 bg-white transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
        <span className={`block h-0.5 w-6 bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
        <span className={`block h-0.5 w-6 bg-white transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
      </button>

      {/* Mobile dropdown */}
      {open && (
        <div className="md:hidden absolute top-full left-0 right-0 mx-4 mt-1 bg-[#1c0a02]/95 backdrop-blur-md rounded-xl border border-white/10 shadow-2xl p-4 flex flex-col gap-1 z-50">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.target)}
              className="text-left text-white hover:bg-white/10 font-montserrat transition-colors px-4 py-3 rounded-lg text-base font-medium"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go(paths.login)}
            className="mt-1 px-4 py-3 bg-white text-black font-montserrat rounded-lg font-semibold hover:bg-[#019D9D] hover:text-white transition-all"
          >
            Log In
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;